import { motion, AnimatePresence } from 'framer-motion'
import { ArrowRight, Ban } from 'lucide-react'
import { formatDealValue } from '@/utils/dealDisplay'
import { cn } from '@/utils/cn'
import { stageConfig } from '@/components/Deals/deals-constants'
import { getColumnMetrics } from '@/components/Deals/kanbanColumnMetrics'
import { KanbanCardV2 } from '@/components/DealsV2/KanbanCardV2'
import type { Deal, DealStage } from '@/types'
import type { useDealsDnd } from '@/hooks/useDealsDnd'
import { forwardRef } from 'react'

type DndState = ReturnType<typeof useDealsDnd>

export type KanbanColumnV2Props = {
  stage: DealStage
  deals: Deal[]
  dnd: DndState
  draggedDeal: Deal | null | undefined
  onSelectDeal: (deal: Deal) => void
  onMoveStage?: (dealId: string, newStage: DealStage) => void
  areValuesVisible?: boolean
}

export const KanbanColumnV2 = forwardRef<HTMLDivElement, KanbanColumnV2Props>(
  function KanbanColumnV2(
    { stage, deals, dnd, draggedDeal, onSelectDeal, onMoveStage, areValuesVisible = false },
    ref,
  ) {
    const config = stageConfig[stage]
    const isDragOver = dnd.draggedOverStage === stage
    const isSameColumn = Boolean(dnd.isDragging && draggedDeal?.etapa === stage)
    const isBlocked = Boolean(dnd.isDragging && dnd.blockedStage === stage)
    const isInvalidDrop = isSameColumn || isBlocked
    const metrics = getColumnMetrics(deals)

    return (
      <div
        ref={ref}
        style={{ '--stage-color': config.color } as React.CSSProperties}
        className={cn(
          'deals-v2-column flex shrink-0 flex-col gap-3 rounded-2xl p-1 transition-shadow duration-200',
          dnd.isDragging && 'kanban-column-dnd',
          isDragOver && !isInvalidDrop && 'deals-v2-column--drop-ready kanban-column-active',
          isDragOver && isInvalidDrop && 'deals-v2-column--drop-invalid kanban-column-invalid',
        )}
        onDragOver={dnd.handleDragOver}
        onDragEnter={(e) => dnd.handleDragEnter(e, stage)}
        onDragLeave={(e) => dnd.handleDragLeave(e, stage)}
        onDrop={(e) => dnd.handleDrop(e, stage)}
      >
        <header className='deals-v2-column-header overflow-hidden rounded-2xl border border-[var(--border)] p-3.5 shadow-sm'>
          <div className='flex items-start justify-between gap-2'>
            <div className='min-w-0'>
              <p className='truncate text-sm font-semibold text-text-primary'>{config.label}</p>
              <p
                className={cn(
                  'mt-1 text-[11px] font-medium',
                  metrics.slaBreaches > 0 ? 'text-warning' : 'text-text-muted',
                )}
              >
                {metrics.slaLabel}
              </p>
            </div>
            <span
              className='rounded-full px-2.5 py-1 text-xs font-bold tabular-nums text-white'
              style={{ backgroundColor: config.color }}
            >
              {metrics.count}
            </span>
          </div>

          <div className='deals-v2-column-stats mt-3 overflow-hidden rounded-lg'>
            <div className='grid grid-cols-3 divide-x divide-[var(--border)]'>
              <ColumnStat
                label='Valor'
                value={formatDealValue(metrics.value, areValuesVisible, true)}
              />
              <ColumnStat
                label='Dias na etapa'
                value={`${metrics.avgAging}d`}
                title='Tempo médio nesta etapa'
              />
              <ColumnStat
                label='SLA'
                value={metrics.slaBreaches > 0 ? `${metrics.slaBreaches} risco` : 'OK'}
                warn={metrics.slaBreaches > 0}
              />
            </div>
          </div>

          {dnd.isDragging && (
            <div
              className={cn(
                'mt-3 flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-[11px] font-medium',
                isInvalidDrop
                  ? 'bg-danger/10 text-danger'
                  : isDragOver
                    ? 'bg-[var(--brand-primary-muted)] text-[var(--brand-primary)]'
                    : 'bg-[var(--bg-dark)] text-text-muted',
              )}
            >
              {isInvalidDrop ? <Ban size={12} /> : <ArrowRight size={12} />}
              {isInvalidDrop
                ? isBlocked
                  ? (dnd.blockReason?.reason ?? 'Bloqueado')
                  : 'Coluna atual'
                : isDragOver
                  ? 'Solte aqui'
                  : 'Arraste para mover'}
            </div>
          )}
        </header>

        <div className='flex min-h-[120px] flex-col gap-2.5 px-0.5 pb-2'>
          <AnimatePresence mode='popLayout'>
            {deals.map((deal, i) => (
              <motion.div
                key={deal.id}
                layout
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ delay: Math.min(i * 0.025, 0.2), duration: 0.22 }}
              >
                {onMoveStage ? (
                  <div
                    draggable
                    onDragStart={(e) => dnd.handleDragStart(e, deal.id)}
                    onDragEnd={dnd.handleDragEnd}
                  >
                    <KanbanCardV2
                      deal={deal}
                      stageColor={config.color}
                      onClick={() => onSelectDeal(deal)}
                      onMoveStage={onMoveStage}
                      areValuesVisible={areValuesVisible}
                      isDragging={dnd.draggedDealId === deal.id}
                    />
                  </div>
                ) : (
                  <KanbanCardV2
                    deal={deal}
                    stageColor={config.color}
                    onClick={() => onSelectDeal(deal)}
                    areValuesVisible={areValuesVisible}
                    isDragging={dnd.draggedDealId === deal.id}
                  />
                )}
              </motion.div>
            ))}
          </AnimatePresence>

          {deals.length === 0 && !dnd.isDragging && (
            <div className='rounded-2xl border border-dashed border-[var(--border)] bg-[var(--bg-dark)]/50 px-4 py-8 text-center'>
              <p className='text-xs text-text-muted'>Nenhum deal</p>
            </div>
          )}

          {deals.length === 0 && dnd.isDragging && (
            <div
              className={cn(
                'kanban-drop-placeholder rounded-2xl',
                isDragOver && !isInvalidDrop && 'active',
                isInvalidDrop && 'invalid',
              )}
            >
              <p>{isInvalidDrop ? 'Inválido' : 'Solte aqui'}</p>
            </div>
          )}
        </div>
      </div>
    )
  },
)

function ColumnStat({
  label,
  value,
  warn,
  title,
}: {
  label: string
  value: string
  warn?: boolean
  title?: string
}) {
  return (
    <div className='min-w-0 px-2.5 py-2' title={title}>
      <p className='truncate text-[10px] leading-none text-text-muted'>{label}</p>
      <p
        className={cn(
          'mt-1.5 truncate text-xs font-semibold tabular-nums leading-none',
          warn ? 'text-warning' : 'text-text-primary',
        )}
      >
        {value}
      </p>
    </div>
  )
}
